import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const HeroVisual = () => {
  return (
    <div className="relative w-full aspect-[4/3] max-w-[600px] mx-auto lg:ml-auto flex items-center justify-center pointer-events-none">
      
      {/* Background Ambient Glow */}
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 via-transparent to-purple-600/10 rounded-full filter blur-[80px] animate-pulse opacity-80"></div>
      
      {/* Background Tech Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_20%,transparent_100%)]"></div>

      {/* Main Dashboard Window */}
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative w-full md:w-[110%] z-20 bg-[#0f1423]/90 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.6)] overflow-hidden pointer-events-auto"
      >
        {/* Window Header */}
        <div className="h-10 bg-[#151b2b] border-b border-white/5 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
            PRBoost Analytics Engine
          </div>
        </div>

        {/* Dashboard Body */}
        <div className="p-6 md:p-8">
          {/* Header Stats */}
          <div className="flex justify-between items-end mb-8">
            <div>
              <h3 className="text-white font-bold text-xl mb-1">Authority Growth</h3>
              <p className="text-slate-400 text-sm">Real-time domain metrics</p>
            </div>
            <div className="text-right">
              <div className="text-emerald-400 font-bold text-2xl flex items-center gap-1.5 justify-end tracking-tight">
                <SafeIcon icon={FiIcons.FiTrendingUp} className="text-xl" /> +248%
              </div>
              <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-1">90 Day Trend</p>
            </div>
          </div>

          {/* Chart Area */}
          <div className="relative w-full h-32 md:h-40 mb-8 border-b border-white/5 pb-4">
            {/* Chart Grid Lines */}
            <svg viewBox="0 0 400 120" className="absolute inset-0 w-full h-full opacity-20">
              <line x1="0" y1="30" x2="400" y2="30" stroke="#475569" strokeDasharray="4 4" />
              <line x1="0" y1="60" x2="400" y2="60" stroke="#475569" strokeDasharray="4 4" />
              <line x1="0" y1="90" x2="400" y2="90" stroke="#475569" strokeDasharray="4 4" />
            </svg>

            {/* Main SVG Chart */}
            <svg viewBox="0 0 400 120" className="relative w-full h-full overflow-visible z-10" preserveAspectRatio="none">
              <defs>
                <linearGradient id="chartGlow" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                </linearGradient>
              </defs>
              
              {/* Fill Area */}
              <motion.path
                d="M0,100 C50,100 80,80 120,85 C160,90 200,40 250,50 C300,60 350,20 400,10 L400,120 L0,120 Z"
                fill="url(#chartGlow)"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
              
              {/* Line Curve */}
              <motion.path
                d="M0,100 C50,100 80,80 120,85 C160,90 200,40 250,50 C300,60 350,20 400,10"
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                style={{ filter: 'drop-shadow(0px 4px 6px rgba(59, 130, 246, 0.4))' }}
              />
            </svg>
          </div>

          {/* Bottom Metrics Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/5 rounded-xl p-4">
              <div className="text-slate-500 text-[10px] uppercase tracking-widest font-bold mb-1">Domain Rating</div>
              <div className="text-white font-bold text-lg md:text-xl">74 <span className="text-emerald-400 text-xs ml-1 font-medium">+12</span></div>
            </div>
            <div className="bg-white/5 border border-white/5 rounded-xl p-4">
              <div className="text-slate-500 text-[10px] uppercase tracking-widest font-bold mb-1">Ref. Domains</div>
              <div className="text-white font-bold text-lg md:text-xl">1.2K <span className="text-emerald-400 text-xs ml-1 font-medium">+304</span></div>
            </div>
            <div className="bg-white/5 border border-white/5 rounded-xl p-4">
              <div className="text-slate-500 text-[10px] uppercase tracking-widest font-bold mb-1">Org. Traffic</div>
              <div className="text-white font-bold text-lg md:text-xl">84.5K <span className="text-emerald-400 text-xs ml-1 font-medium">+24%</span></div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating UI Elements */}

      {/* Floating Card 1: Placement Secured */}
      <motion.div 
        className="absolute -right-2 md:-right-12 top-16 bg-[#151b2b]/95 backdrop-blur-md border border-white/10 p-3 rounded-xl shadow-2xl flex items-center gap-3 z-30"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0, y: [-5, 5, -5] }}
        transition={{ 
          opacity: { duration: 0.5, delay: 1 },
          x: { duration: 0.5, delay: 1 },
          y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 border border-emerald-500/30">
          <SafeIcon icon={FiIcons.FiCheck} className="text-xl" />
        </div>
        <div className="pr-2">
          <div className="text-[9px] text-slate-400 uppercase tracking-widest font-bold mb-0.5">Placement Secured</div>
          <div className="text-sm text-white font-medium flex items-center gap-2">
            Forbes.com 
            <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.5 rounded font-bold border border-emerald-500/20">DR 94</span>
          </div>
        </div>
      </motion.div>

      {/* Floating Card 2: Ecosystem Index */}
      <motion.div 
        className="absolute -left-4 md:-left-12 bottom-24 bg-[#151b2b]/95 backdrop-blur-md border border-white/10 p-3 rounded-xl shadow-2xl flex items-center gap-3 z-30"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0, y: [5, -5, 5] }}
        transition={{ 
          opacity: { duration: 0.5, delay: 1.2 },
          x: { duration: 0.5, delay: 1.2 },
          y: { duration: 5, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 border border-purple-500/30">
          <SafeIcon icon={FiIcons.FiGlobe} className="text-xl" />
        </div>
        <div className="pr-2">
          <div className="text-[9px] text-slate-400 uppercase tracking-widest font-bold mb-0.5">Ecosystem Reach</div>
          <div className="text-sm text-white font-medium flex items-center gap-2">
            Google & AI Search
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default HeroVisual;